import { useContext, useRef } from 'react';

import clsx from 'clsx';

import dynamic from 'next/dynamic';

const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);
import { Container } from 'components/Grid';
import LazyContent from 'components/LazyContent';
import ListingScreenshots from 'components/Listing/components/Screenshots';
import Paper from 'components/Paper';
import SoftwareApplicationLD from 'components/SoftwareApplicationLD/SoftwareApplicationLD';
import TrustBanner from 'components/TrustBanner';
import Typography from 'components/Typography';
import UserVerdict from 'components/UserVerdict';
import { useAppContext } from 'context/AppContext';
import { CompareContext } from 'context/CompareContext';
import { EventContext } from 'context/EventContext';
import { getVideoStructuredData } from 'helpers/SEOHelper';
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect';
import useViewport from 'hooks/useViewport';

import { SECTIONS, generateNavsForUserVerdicts } from '../../constants';
import AddToCompare from '../AddToCompare';
import FeaturesIntegrations from '../FeaturesIntegrations';
import GridContainer from '../GridContainer';
import GridItem from '../GridItem';
import ListingTopHeader from '../ListingTopHeader';
import ListingVideos from '../ListingVideos';
import Overview from '../Overview';
import Pricing from '../Pricing';
import ReviewsSection from '../ReviewsSection';
import SectionTitle from '../SectionTitle';
import Sidebar from '../Sidebar';
import StyleOnScroll from '../StyleOnScroll';
import UserReviews from '../UserReviews';
import AlternativeCard from './components/AlternativeCard';
import CustomSectionTitle from './components/SectionTitle';

import classes from './CompareMobileLayout.module.scss';

const MAX_NUMBER_OF_COMPARISONS = 4;
const TOP_OFFSET = 68; // Size of: Breadcrumbs + Content's marginTop
const LISTING_TOP_HEADER_HIDE_OFFSET = TOP_OFFSET + 112;
const SCROLL_THRESHOLD = 142;

let timeoutScroll;

const Element = (props) => <div {...props} className="sectionItem" />;

const CompareMobileLayout = () => {
  const {
    listings = [],
    features = [],
    integrations = [],
    alternatives = [],
    comparisonReviews = {},
    title,
  } = useContext(CompareContext);
  const { isCypressTakingSnapshots } = useAppContext();
  const { is } = useViewport();
  const titleRef = useRef();
  const isXs = is('xs');
  const shouldRenderAddToCompare = listings.length < MAX_NUMBER_OF_COMPARISONS;
  const GA_LABEL = listings.length > 1 ? 'ua_compare' : 'ua_compare_single';
  const hasComparisonReviews =
    comparisonReviews.first || comparisonReviews.last;
  const stickyMobileTopHeaderRef = useRef(null);
  const stickyMobileTopHeaderRef1 = useRef(null);
  const shouldRenderVideoSection = listings.some(
    (listing) => listing.videos && !!listing.videos.length
  );
  const shouldRenderUserVerdicts = listings.every(
    (listing) =>
      listing.video_comparison_reviews &&
      !!listing.video_comparison_reviews.length
  );

  const navSections = generateNavsForUserVerdicts(shouldRenderUserVerdicts);

  const handleScroll = () => {
    const { scrollY } = window;

    if (scrollY > SCROLL_THRESHOLD && stickyMobileTopHeaderRef.current) {
      titleRef.current.dataHeight =
        titleRef.current.dataHeight ||
        titleRef.current.getBoundingClientRect().height;
      // Conditional below fixes issue of empty space when scrolling down.
      // In order to resolve issue with empty space when scrolling up
      // an event listener for the direction of the scroll is needed to
      // control the opacity of the listing top header (possible performance blocker)
      // i.e. scrollUp && scrollY < LISTING_TOP_HEADER_HIDE_OFFSET -> opacity = 1
      if (scrollY > LISTING_TOP_HEADER_HIDE_OFFSET) {
        stickyMobileTopHeaderRef.current.style.opacity = 0;
      }
      if (timeoutScroll) {
        clearTimeout(timeoutScroll);
      }
      timeoutScroll = setTimeout(() => {
        timeoutScroll = null;
        stickyMobileTopHeaderRef.current.style.opacity = 1;
      }, 100);

      stickyMobileTopHeaderRef.current.style.top = `${scrollY - titleRef.current.dataHeight - TOP_OFFSET}px`;
    } else if (stickyMobileTopHeaderRef.current) {
      stickyMobileTopHeaderRef.current.style.top = 'unset';
      stickyMobileTopHeaderRef.current.style.opacity = 1;
    }
  };

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', () => handleScroll);
    };
  }, []);

  return (
    <>
      <Container className={classes.container} ref={titleRef}>
        <TrustBanner />
        <header>
          <Typography
            component="h1"
            variant="heading-lg"
            className={classes.title}
          >
            {title}
          </Typography>
        </header>
      </Container>
      <div className={classes.contentContainer}>
        {!isXs && (
          <div className={classes.sideMenuContainer}>
            <StyleOnScroll
              className={clsx({
                [classes.stickySidebarContainer]: !isCypressTakingSnapshots,
              })}
              onScrollClassName={classes.stickySidebarContainerNoMargin}
              containerRef={stickyMobileTopHeaderRef1}
              scrollThreshold={100}
            >
              <Sidebar navSections={Object.values(navSections)} />
            </StyleOnScroll>
          </div>
        )}
        <div
          className={clsx(classes.listingContainer, 'gdm-container-mobile', {
            [classes.withOverflow]: !isCypressTakingSnapshots,
          })}
        >
          <div
            className={clsx(
              classes.column,
              classes.customSections,
              classes.appsContainer,
              classes[`with${listings.length}Columns`]
            )}
          >
            <StyleOnScroll
              className={clsx(classes.listingTopHeaderWrapper, {
                [classes.withSticky]: !isCypressTakingSnapshots,
              })}
              onScrollClassName={
                isCypressTakingSnapshots ? '' : classes.scrollTopHeader
              }
              containerRef={stickyMobileTopHeaderRef}
            >
              <GridContainer className={classes.listingTopHeaderContainer}>
                {listings.map((listing, index) => (
                  <GridItem key={`listing-header-content-${listing.id}`}>
                    <ListingTopHeader
                      listing={listing}
                      numberOfColumns={listings.length}
                      shouldRenderCloseButton={listings.length > 2}
                      listingPosition={index}
                    />
                  </GridItem>
                ))}
              </GridContainer>
            </StyleOnScroll>
            <Element id={navSections.overview.id}>
              <SectionTitle
                label={SECTIONS.overview.name}
                icon={SECTIONS.overview.icon}
                columns={listings.length}
              />
              <Overview listings={listings} />
            </Element>
            {shouldRenderUserVerdicts && (
              <Element
                name={navSections.verdicts.id}
                id={navSections.verdicts.id}
              >
                <SectionTitle
                  label={SECTIONS.verdicts.name}
                  icon={SECTIONS.verdicts.icon}
                  columns={listings.length}
                />
                <GridContainer
                  className={clsx('UserVerdicts', classes.userVerdicts)}
                >
                  {listings.map((listing, index) => {
                    const videoReview = listing.video_comparison_reviews[0];

                    return (
                      <>
                        <GridItem
                          className="UserVerdict"
                          key={`${listing.name}-verdict`}
                        >
                          <Paper
                            elevation={0}
                            className={classes.userVerdictContainer}
                          >
                            <EventContext
                              value={{
                                engagementComponent:
                                  'product_vs_product_video_reviews_card',
                                name: 'engagement_product_click',
                              }}
                            >
                              <UserVerdict
                                listing={listing}
                                videoReview={videoReview}
                              />
                            </EventContext>
                          </Paper>
                        </GridItem>
                        {index === 0 && (
                          <script
                            type="application/ld+json"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: getVideoStructuredData({
                                ...videoReview,
                                ...videoReview.video_source,
                                published: videoReview.published_on,
                              }),
                            }}
                          />
                        )}
                      </>
                    );
                  })}
                </GridContainer>
              </Element>
            )}
            <Element id={navSections.screenshots.id}>
              <SectionTitle
                label={SECTIONS.screenshots.name}
                icon={SECTIONS.screenshots.icon}
                columns={listings.length}
              />
              <LazyContent
                data-testid="lazy-screenshots"
                columns={listings.length}
                className={classes.lazyContentWrapper}
              >
                <GridContainer>
                  {listings.map((listing) => (
                    <GridItem key={`screenshots-content-${listing.id}`}>
                      {shouldRenderVideoSection && (
                        <Paper
                          className={classes.lazyContentWrapper}
                          elevation={0}
                          square={true}
                        >
                          <ListingVideos listing={listing} />
                        </Paper>
                      )}
                      <Paper
                        className={classes.lazyContentWrapper}
                        elevation={0}
                        square={true}
                      >
                        <EventContext
                          value={{ engagementComponent: 'product_media' }}
                        >
                          <ListingScreenshots
                            listingName={listing.name}
                            screenshots={listing.screenshots}
                          />
                        </EventContext>
                      </Paper>
                    </GridItem>
                  ))}
                </GridContainer>
              </LazyContent>
            </Element>
            {!isCypressTakingSnapshots && (listings.length < 3 || !isXs) && (
              <div className={classes.serviceForm}>
                <div style={{ minHeight: 300 }}>
                  <InlineForm
                    formId="4mPBqrfw206mj7S1JPfygc"
                    gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                    formTemplate="INLINE_CARD"
                    headline="Send this comparison chart to my inbox"
                    subHeading=""
                    buttonText="Submit"
                    successMessage="Your request will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder."
                    contextProps={{
                      pageType: 'compare',
                      pagePlacement: 'inline',
                    }}
                    tracking={{
                      formName: 'DMBUY_2022_GA_Blog_Acq',
                      contentCategory: 'Inline Modal',
                      contentRequest: 'Send To Me_Comparison',
                    }}
                  />
                </div>
              </div>
            )}
            <Element id={navSections.pricing.id}>
              <SectionTitle
                label={SECTIONS.pricing.name}
                icon={SECTIONS.pricing.icon}
                columns={listings.length}
              />
              <LazyContent
                data-testid="lazy-pricing"
                renderImmediately={true}
                minHeight={230}
                columns={listings.length}
                className={classes.lazyContentWrapper}
              >
                <Pricing listings={listings} columns={listings.length} />
              </LazyContent>
            </Element>
            <Element id={navSections.reviews.id}>
              <SectionTitle
                label={SECTIONS.reviews.name}
                icon={SECTIONS.reviews.icon}
                columns={listings.length}
              />
              <LazyContent
                data-testid="lazy-reviews"
                renderImmediately={true}
                minHeight={1400}
                columns={listings.length}
                className={classes.lazyContentWrapper}
              >
                <UserReviews listings={listings} isMobileTablet={true} />
              </LazyContent>
            </Element>
            <Element id={navSections.features.id}>
              <SectionTitle
                label={SECTIONS.features.name}
                icon={SECTIONS.features.icon}
                columns={listings.length}
              />
              <LazyContent
                data-testid="lazy-features"
                renderImmediately={true}
                minHeight={430}
                columns={listings.length}
                className={classes.lazyContentWrapper}
              >
                <FeaturesIntegrations
                  listings={listings}
                  section={features}
                  category={SECTIONS.features.slug}
                  gaLabel={GA_LABEL}
                  isMobileTabletLayout={true}
                />
              </LazyContent>
            </Element>
            <Element id={navSections.integrations.id}>
              <SectionTitle
                label={SECTIONS.integrations.name}
                icon={SECTIONS.integrations.icon}
                columns={listings.length}
              />
              <LazyContent
                data-testid="lazy-integrations"
                renderImmediately={true}
                minHeight={380}
                columns={listings.length}
                className={classes.lazyContentWrapper}
              >
                <FeaturesIntegrations
                  listings={listings}
                  section={integrations}
                  category={SECTIONS.integrations.slug}
                  gaLabel={GA_LABEL}
                  isMobileTabletLayout={true}
                />
              </LazyContent>
            </Element>
          </div>
          {shouldRenderAddToCompare && (
            <div className={classes.rightSideContainer}>
              <div
                className={clsx(
                  classes.column,
                  classes.similarAppsContainer,
                  {
                    [classes.similarAppsContainer1Column]:
                      listings.length === 1,
                  },
                  'gdm-apps-sticky'
                )}
              >
                <AddToCompare listings={listings} alternatives={alternatives} />
              </div>
            </div>
          )}
        </div>
      </div>
      {listings.map((listing, index) => (
        <SoftwareApplicationLD listing={listing} key={index} />
      ))}
      <div
        className={clsx('sectionItem', classes.alternatives)}
        id={navSections.alternatives.id}
      >
        <CustomSectionTitle
          icon={SECTIONS.alternatives.icon}
          title={SECTIONS.alternatives.name}
          tagline="Explore similar apps"
          className={classes.alternativesSectionTitle}
        />
        {listings[0].alternatives.slice(0, 3).map((alternative, i) => (
          <LazyContent
            data-testid="lazy-alternatives"
            renderImmediately={true}
            minHeight={282}
            className={clsx(classes.lazyContentWrapper, {
              [classes.lazyContentWrapperAlternatives]: i !== 2,
            })}
            key={`lazy-content-alternative-${alternative.slug}`}
          >
            <AlternativeCard
              listingPosition={i}
              className={i !== 2 ? classes.alternativeCard : ''}
              compareUrl={
                listings.length < 4 ? alternative.compare_url : undefined
              }
              ctaUrl={alternative.cta_url}
              detailUrl={alternative.detail_url}
              isPremium={alternative.is_premium}
              isCompareUrlSeo={alternative.compare_page.is_seo}
              key={`alternative-card-${alternative.id}`}
              logoUrl={alternative.logo}
              name={alternative.name}
              productId={alternative.global_product_id}
              rating={alternative.rating_avg}
              reviewsNum={alternative.num_reviews}
              reviewsUrl={alternative.reviews_url}
              shortDescription={alternative.short_description}
              tagline={alternative.tagline}
            />
          </LazyContent>
        ))}
      </div>
      {hasComparisonReviews && (
        <ReviewsSection
          title="User reviews that mention these apps"
          reviews={comparisonReviews}
          listings={listings}
          mobile={true}
          lazy={true}
        />
      )}
      <hr className={classes.hybridEmptyDescription} />
    </>
  );
};

export default CompareMobileLayout;
